import { Divider, Skeleton, Spin } from "antd"
import { useEffect, useMemo, useState } from "react"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import buildingIcon from "assets/svg/Buildings.svg"
import departmentIcon from "assets/images/department-icon.png"
import jobIcon from "assets/images/job.svg"
import {
  IClockedInStatusRes,
  IDutyLocationRes,
  IEmployeeMetricsRes,
  IShiftSetupRes,
  ITimeSheetRes,
} from "./interface/shiftData"
import { useAuthStore } from "stores/auth"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import Map from "../../../assets/images/Map_container.svg"
import { DepartmentInfoCard } from "./components/DepartmentInfoCard"
import {
  EmployeeDashboardCardWrapper,
  EmployeeDashboardCard,
} from "./components/EmployeeDashboardCardWrapper"
import { formatDelta, getBadgeColors } from "./utils"
import { useGet } from "utils/useFetch"
import { IPersonalDetails } from "../personal/interface/Personal"
import EmployeeClockInOut from "./employee-clock-in/EmployeeClockInOut"
import { AssignedShiftsCalendar } from "./shift-calender/AssignedShiftsCalender"
import { Timesheets } from "./time-sheet/TimeSheets"
import { ShiftSwap } from "./shift-swap/ShiftSwap"
import OvertimeSubmission from "./overtime-submission/OvertimeSubmission"
import { getStaticMapUrl } from "components/GeoPicker"
import { Skeleton as AppSkeleton } from "components/new-stuff/ui/Skeleton"
import SelectDutyLocationWrapper, {
  DutyLocationItem,
} from "./components/SelectDutyLocationWrapper"

type IPersonalDetailsRes = IBaseRes<IPersonalDetails>

const TimeAndAttendance = () => {
  const [selectedLocation, setSelectedLocation] = useState<
    number | "workplace"
  >("workplace")
  const [groupBy, setGroupBy] = useState<"day" | "week" | "month">("week")
  const { user } = useAuthStore((state) => state)

  const { data: employee, isLoading: isLoadingEmployee } =
    useGet<IPersonalDetailsRes>({
      url: "employee/details/view/index_personal_info",
      instanceKey: "newArchAxiosInstance",
    })

  // ─── Query keys ──────────────────────────────────────────────────────

  const startDate = "2020-01-01"
  const endDate = new Date().toISOString().split("T")[0]

  const isWorkplace = selectedLocation === "workplace"

  const dutyLocationId =
    selectedLocation === "workplace" ? null : (selectedLocation as number)

  const timesheetQueryKey = isWorkplace
    ? `/v1/time-attendance/employee/timesheets?startDate=${startDate}&endDate=${endDate}&limit=1000&groupBy=${groupBy}`
    : `/v1/time-attendance/employee/timesheets?startDate=${startDate}&endDate=${endDate}&limit=1000&groupBy=${groupBy}&dutyLocationId=${dutyLocationId}`

  // ─── Data fetching ────────────────────────────────────────────────────────

  const { isLoading: isLoadingDutyLocation, data: dutyLocationData } =
    useGet<IDutyLocationRes>({
      url: "/v1/time-attendance/employee/duty-locations",
      instanceKey: "ewaAxiosInstance",
    })

  const { data: clockinStatus } = useGet<IClockedInStatusRes>({
    url: "/v1/time-attendance/shift-plans/clock-in/check-status",
    instanceKey: "ewaAxiosInstance",
  })

  const workplaceId = dutyLocationData?.data?.workplace?.id ?? null

  const { isLoading: isLoadingShiftSetup, data: shiftSetupData } =
    useGet<IShiftSetupRes>({
      url: isWorkplace
        ? `/v1/time-attendance/employee/shift-setup?workplaceId=${workplaceId}`
        : `/v1/time-attendance/employee/shift-setup?dutyLocationId=${dutyLocationId}&workplaceId=${workplaceId}`,
      instanceKey: "ewaAxiosInstance",
      queryKey: ["shift-setup", String(selectedLocation)],
      enabled: !isLoadingDutyLocation,
    })

  const { isLoading: isLoadingMetrics, data: metricsData } =
    useGet<IEmployeeMetricsRes>({
      url: `/v1/time-attendance/employee/analytics?period=90d&workplaceId=${workplaceId}`,
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceId,
    })

  const { data: timesheetData, isLoading: isLoadingTimesheet } =
    useGet<ITimeSheetRes>({
      url: timesheetQueryKey,
      instanceKey: "ewaAxiosInstance",
    })

  // ─── Derived state ────────────────────────────────────────────────────────

  const metrics = metricsData?.data?.metrics
  const overtimeAllowed = shiftSetupData?.data?.overtimeAllowed
  const currentCounters = metricsData?.data?.counters?.current
  const [isAnyTimePlan, setIsAnyTimePlan] = useState(false)
  const [planAllowsSwapping, setPlanAllowsSwapping] = useState(false)
  const shiftBadge = getBadgeColors(metrics?.shiftAdherence, true)
  const absenteeismBadge = getBadgeColors(metrics?.absenteeismRate, false)
  const timeAdherenceBadge = getBadgeColors(metrics?.timeAdherence, true)

  const today = new Date()
  const todayDate = today.toISOString().split("T")[0]
  const todayDayOfWeek = today
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase()

  const hasShiftToday =
    isLoadingDutyLocation || isLoadingShiftSetup
      ? true
      : shiftSetupData?.data?.shiftAssignment?.some(
          (a) =>
            a.isAnyDayPermitted ||
            a.dayOfWeek === todayDayOfWeek ||
            a.date?.split("T")[0] === todayDate
        ) ?? false

  const dutyLocationOptions = useMemo(
    () => [
      {
        id: "workplace",
        name: dutyLocationData?.data?.workplace.displayName || "",
        addressLine1: dutyLocationData?.data?.workplace.fullAddress || "",
        latitude: dutyLocationData?.data?.workplace?.latitude,
        longitude: dutyLocationData?.data?.workplace?.longitude,
      },
      ...(dutyLocationData?.data?.workplace.dutyLocations?.map((i) => ({
        id: i?.id,
        name: i?.name,
        addressLine1: i?.addressLine1 ?? "",
        latitude: i?.latitude ?? dutyLocationData?.data?.workplace?.latitude,
        longitude: i?.longitude ?? dutyLocationData?.data?.workplace?.longitude,
      })) || []),
    ],
    [dutyLocationData]
  )

  const mapLocation = dutyLocationOptions.find(
    (loc) => loc.id === selectedLocation
  )
  const mapLat = mapLocation?.latitude
  const mapLng = mapLocation?.longitude

  const employeeDashboardCards = [
    {
      percentage: `${currentCounters?.totalAttendedShifts ?? 0} of ${
        currentCounters?.totalScheduledShifts ?? 0
      }`,
      badge: formatDelta(metrics?.shiftAdherence),
      badgeColor: shiftBadge.badgeColor,
      badgeBgColor: shiftBadge.badgeBgColor,
      title: "Shifts completed this week",
      message: "Number of scheduled shifts that you have attended this week.",
    },
    {
      percentage: `${(metrics?.absenteeismRate?.value ?? 0).toFixed(0)}%`,
      badge: formatDelta(metrics?.absenteeismRate),
      badgeColor: absenteeismBadge.badgeColor,
      badgeBgColor: absenteeismBadge.badgeBgColor,
      title: "Historical Absenteeism Rate",
      message: "Percentage of scheduled shifts in weeks preceding this week.",
    },
    {
      percentage: `${(metrics?.timeAdherence?.value ?? 0).toFixed(0)}%`,
      badge: formatDelta(metrics?.timeAdherence),
      badgeColor: timeAdherenceBadge.badgeColor,
      badgeBgColor: timeAdherenceBadge.badgeBgColor,
      title: "Time Adherence",
      message:
        "Difference between total scheduled hours and actual hours worked.",
    },
  ]

  if (isLoadingDutyLocation || isLoadingShiftSetup) {
    return (
      <DashboardWrapper>
        <div
          className="flex flex-col gap-4 py-4"
          aria-busy="true"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <AppSkeleton
                key={i}
                variant="block"
                width="100%"
                height="106px"
              />
            ))}
          </div>
          <AppSkeleton
            variant="block"
            width="100%"
            height="280px"
          />
        </div>
      </DashboardWrapper>
    )
  }

  return (
    <DashboardWrapper>
      <div className="ta-scope -m-2 p-3">
        <div className="relative w-full h-52 overflow-hidden">
          <div
            className="w-full h-full bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: `url(${getStaticMapUrl(
                Number(mapLat),
                Number(mapLng)
              )})`,
            }}
          />
          <div className="absolute inset-x-0 bottom-0 h-16 pointer-events-none bg-gradient-to-t from-white to-transparent z-10" />
        </div>
        <div className="bg-white p-4 mb-3 flex items-center">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-5">
              <PersonAvatar
                src={user?.userAvatarFileUrl}
                alt={user?.userAvatarFileUrl}
                className="rounded-lg w-[60px] h-[60px] object-cover"
              />
              <div>
                <h5 className="text-base text-padeBlack font-semibold">
                  Welcome, {user?.userFirstName}
                </h5>
                <p className="text-[#62748E] text-sm">
                  Your time & attendance tracking page.
                </p>
              </div>
            </div>
            {isLoadingEmployee ? (
              <Skeleton
                className="mt-3"
                active
                title={false}
                paragraph={{ rows: 1, width: "100%" }}
              />
            ) : (
              <div className="flex items-center gap-1 flex-wrap">
                <DepartmentInfoCard
                  icon={departmentIcon}
                  department={employee?.data?.department}
                />
                <Divider
                  type="vertical"
                  className="bg-slate-300"
                />
                <DepartmentInfoCard
                  icon={jobIcon}
                  department={employee?.data?.employee_title}
                />
                <div className="hidden sm:block">
                  <Divider
                    type="vertical"
                    className="bg-slate-300"
                  />
                </div>
                <div className="hidden sm:block">
                  <DepartmentInfoCard
                    icon={buildingIcon}
                    department={user?.userOrganisation}
                  />
                </div>
              </div>
            )}

            {!overtimeAllowed && (
              <div className="mb-3">
                {isLoadingMetrics ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div
                        key={i}
                        className="border border-[#E2E8F0] rounded-md p-2 flex-1 mt-2"
                      >
                        <Skeleton
                          active
                          title
                          paragraph={{ rows: 2 }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmployeeDashboardCardWrapper>
                    {employeeDashboardCards.map((card) => (
                      <EmployeeDashboardCard
                        key={card.title}
                        {...card}
                      />
                    ))}
                  </EmployeeDashboardCardWrapper>
                )}
              </div>
            )}
          </div>
          {!overtimeAllowed && (
            <img
              width={230}
              height={230}
              src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/bd4bd1e6-6a42-499b-b74b-822b8ffa0b59.png"
              className="hidden lg:block shrink-0 mt-6"
              loading="lazy"
            />
          )}
        </div>
        {!overtimeAllowed && (
          <div className="bg-white mb-2">
            <SelectDutyLocationWrapper
              isLoadingDutyLocation={isLoadingDutyLocation}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              dutyLocation={dutyLocationOptions as DutyLocationItem[]}
            />
          </div>
        )}
        {overtimeAllowed ? (
          <OvertimeSubmission />
        ) : (
          <>
            {hasShiftToday && (
              <EmployeeClockInOut
                shiftSetupData={shiftSetupData}
                isLoading={isLoadingShiftSetup}
                timesheetData={timesheetData}
                clockinStatus={clockinStatus}
                groupBy={groupBy}
                timesheetQueryKey={timesheetQueryKey}
                isWorkplace={isWorkplace}
              />
            )}
            <div className="block xl:flex justify-between items-stretch gap-3">
              <div className="flex-1 min-w-0 overflow-hidden">
                <AssignedShiftsCalendar
                  shiftSetupData={shiftSetupData}
                  planType={(isAnyTime, allowSwapping) => {
                    setIsAnyTimePlan(isAnyTime)
                    setPlanAllowsSwapping(allowSwapping)
                  }}
                  isLoadingShiftSetup={isLoadingShiftSetup}
                  dutyLocationId={dutyLocationId}
                />
              </div>
              {!isAnyTimePlan && (
                <ShiftSwap
                  planAllowsSwapping={planAllowsSwapping}
                  isLoading={isLoadingShiftSetup}
                />
              )}
            </div>
            <Timesheets
              shiftSetupData={shiftSetupData}
              groupBy={groupBy}
              timesheetQueryKey={timesheetQueryKey}
              timesheetData={timesheetData}
              isLoadingTimesheet={isLoadingTimesheet}
              setGroupBy={setGroupBy}
            />
          </>
        )}
      </div>
    </DashboardWrapper>
  )
}

export default TimeAndAttendance
